import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { clientApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Pencil,
  Trash2,
  Users,
  Briefcase,
  DollarSign,
  Calendar,
  Building2,
  FileText,
  Download,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const stageColors = {
  Lead: 'bg-blue-100 text-blue-800',
  Qualified: 'bg-purple-100 text-purple-800',
  Estimate: 'bg-yellow-100 text-yellow-800',
  Proposal: 'bg-orange-100 text-orange-800',
  Negotiation: 'bg-pink-100 text-pink-800',
  Won: 'bg-green-100 text-green-800',
  Lost: 'bg-red-100 text-red-800',
};

const statusColors = {
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    is_general_contractor: false,
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      const [clientRes, historyRes] = await Promise.all([
        clientApi.getOne(id),
        clientApi.getHistory(id),
      ]);
      setClient(clientRes.data);
      setLeads(historyRes.data.leads || []);
      setProjects(historyRes.data.projects || []);
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await clientApi.delete(id);
      toast.success('Client deleted');
      navigate('/clients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleStatementPdf = async () => {
    try {
      const res = await clientApi.getStatementPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to open statement');
    }
  };

  const handleStatementEmail = async () => {
    try {
      const res = await clientApi.sendStatement(id);
      toast.success(res.data.message || 'Statement sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to email statement');
    }
  };

  const openEditDialog = () => {
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      company: client.company || '',
      is_general_contractor: !!client.is_general_contractor,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      notes: client.notes || '',
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error('Last name is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company: formData.company?.trim() || null,
        is_general_contractor: !!formData.is_general_contractor,
        email: formData.email?.trim() || null,
        phone: formData.phone.trim(),
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state || null,
        zip_code: formData.zip_code?.trim() || null,
        notes: formData.notes?.trim() || null,
      };
      await clientApi.update(id, payload);
      toast.success('Client updated');
      setShowEditDialog(false);
      fetchClientData(); // Refresh data
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0]?.msg : msg || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (!client) return null;

  const totalLeadValue = leads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
  const totalProjectValue = projects.reduce((sum, p) => sum + (p.contract_value || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} className="rounded-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{client.name}</h1>
            {client.company && (
              <p className="text-sm font-medium text-muted-foreground">{client.company}</p>
            )}
            <p className="text-muted-foreground">Client since {formatDate(client.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={handleStatementPdf}
            data-testid="client-statement-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Statement
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={handleStatementEmail}
            data-testid="client-statement-email-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Email Statement
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={openEditDialog}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-sm">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Leads</p>
                <p className="text-2xl font-bold">{leads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-sm">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Projects</p>
                <p className="text-2xl font-bold">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-sm">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lead Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totalLeadValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-sm">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Project Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totalProjectValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
              <TabsTrigger value="leads" className="rounded-sm">Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="projects" className="rounded-sm">Projects ({projects.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <Card className="rounded-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {leads.length === 0 && projects.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No activity yet</p>
                  ) : (
                    <div className="space-y-3">
                      {[...leads.slice(0, 3), ...projects.slice(0, 3)]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .slice(0, 5)
                        .map((item) => (
                          <div key={item.id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div className="flex items-center gap-3">
                              {item.stage ? (
                                <Users className="h-4 w-4 text-blue-500" />
                              ) : (
                                <Briefcase className="h-4 w-4 text-green-500" />
                              )}
                              <div>
                                <p className="font-medium text-sm">{item.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {item.stage ? 'Lead' : 'Project'} • {formatDate(item.created_at)}
                                </p>
                              </div>
                            </div>
                            {item.stage ? (
                              <Badge className={`rounded-sm ${stageColors[item.stage] || 'bg-gray-100'}`}>
                                {item.stage}
                              </Badge>
                            ) : (
                              <Badge className={`rounded-sm ${statusColors[item.status] || 'bg-gray-100'}`}>
                                {item.status}
                              </Badge>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="leads" className="space-y-4 mt-4">
              <Card className="rounded-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Leads</CardTitle>
                  <Button 
                    size="sm" 
                    className="rounded-sm"
                    onClick={() => navigate(`/leads/new?client_id=${client.id}`)}
                  >
                    New Lead
                  </Button>
                </CardHeader>
                <CardContent>
                  {leads.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No leads for this client</p>
                  ) : (
                    <div className="space-y-3">
                      {leads.map((lead) => (
                        <Link
                          key={lead.id}
                          to={`/leads/${lead.id}`}
                          className="flex items-center justify-between py-3 px-3 rounded-sm hover:bg-secondary/50 border"
                        >
                          <div>
                            <p className="font-medium">{lead.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {lead.project_type || 'No type'} • {formatDate(lead.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {lead.estimated_value && (
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(lead.estimated_value)}
                              </span>
                            )}
                            <Badge className={`rounded-sm ${stageColors[lead.stage] || 'bg-gray-100'}`}>
                              {lead.stage}
                            </Badge>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="projects" className="space-y-4 mt-4">
              <Card className="rounded-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Projects</CardTitle>
                  <Button 
                    size="sm" 
                    className="rounded-sm"
                    onClick={() => navigate(`/projects/new?client_id=${client.id}`)}
                  >
                    New Project
                  </Button>
                </CardHeader>
                <CardContent>
                  {projects.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No projects for this client</p>
                  ) : (
                    <div className="space-y-3">
                      {projects.map((project) => (
                        <Link
                          key={project.id}
                          to={`/projects/${project.id}`}
                          className="flex items-center justify-between py-3 px-3 rounded-sm hover:bg-secondary/50 border"
                        >
                          <div>
                            <p className="font-medium">{project.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {project.current_phase} • {formatDate(project.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {project.contract_value && (
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(project.contract_value)}
                              </span>
                            )}
                            <Badge className={`rounded-sm ${statusColors[project.status] || 'bg-gray-100'}`}>
                              {project.status}
                            </Badge>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {client.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${client.email}`} className="text-sm hover:text-orange-500">
                    {client.email}
                  </a>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${client.phone}`} className="text-sm hover:text-orange-500">
                    {client.phone}
                  </a>
                </div>
              )}
              {(client.address || client.city || client.state) && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="text-sm">
                    {client.address && <p>{client.address}</p>}
                    {(client.city || client.state || client.zip_code) && (
                      <p>
                        {[client.city, client.state].filter(Boolean).join(', ')}
                        {client.zip_code && ` ${client.zip_code}`}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!client.email && !client.phone && !client.address && (
                <p className="text-muted-foreground text-sm">No contact information</p>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {client.notes && (
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="Smith"
                />
              </div>
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="e.g. Acme Builders (optional)"
                data-testid="client-detail-company-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_general_contractor}
                onChange={(e) => setFormData({ ...formData, is_general_contractor: e.target.checked })}
                className="h-4 w-4 rounded-sm"
                data-testid="client-detail-gc-toggle"
              />
              General Contractor (can add tasks via the project share link)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="rounded-sm mt-1"
                  maxLength={2}
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-sm">
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
